import {useCallback, useState} from "react";
import Textarea from "../../layout/Textarea/Textarea";
import ContentEditable from "../../layout/ContentEditable/ContentEditable";
import {useEdits} from "../../contexts/edits";
import {useSetting} from "../../contexts/setting";
import {useImages} from "../../contexts/images";
import BgTransformer from "../../layout/BgTransformer";
import {useBgUrl} from "../../hooks/useBgUrl";
import GdnLogoBlock from "./GdnLogoBlock";
import GdnAccentControl from "./GdnAccentControl";
import {resolveProductAccentColor} from "../../utils/colors";
import {palettes} from "../../data/palettes";
import {getGdnColorStateKey, getGdnControlColors} from "../../libs/gdnColorCycle";
import {resolveGdnProductName, shouldHideGdnProductName} from "./gdnProductName";
import "../../layout/Tooltip/index.css";
import "./gdn-layout-2.css";
import "./gdn-layout-3.css";

const GDN_BUTTON_LAYOUTS = Object.freeze([1, 2, 3]);

/**
 * Shared body for every Google Display Network ad template. The formats differ
 * only in size, logo treatment, and CSS geometry — so the structure lives here
 * once and each template is a thin wrapper that imports its own `index.css`
 * (which scopes everything under `#<TemplateId>`) and passes its logo variant.
 *
 * The background is v3-served through useBgUrl + <BgTransformer>. Logo
 * selection stays in <GdnLogoBlock>. Title + subtitle use rich editable text;
 * per-template CSS positions them.
 *
 * @param {{
 *   id: string,
 *   zoom: number,
 *   variant?: "horizontal" | "stacked" | "icon" | "responsive-horizontal" | "responsive-stacked",
 *   buttonVariant?: "horizontal" | "stacked" | "icon",
 *   layoutVariants?: {[layout: number]: "horizontal" | "stacked" | "icon" | "responsive-horizontal" | "responsive-stacked" | undefined},
 *   fallbackTransformId?: string,
 *   defaultTransform?: {x: number, y: number, scale: number, rotate?: number, flip?: {x: boolean, y: boolean}},
 *   showContent?: boolean,
 * }} props
 */
const GdnAdTemplate = ({
  id,
  zoom,
  variant = "horizontal",
  buttonVariant = undefined,
  layoutVariants = undefined,
  fallbackTransformId = undefined,
  defaultTransform = undefined,
  showContent = true,
}) => {
  const {
    locked,
    layoutOption,
    isButtonEnabled,
    product,
    gdnColorVariants,
    gdnAccentColor,
    cycleGdnColor,
    clearGdnColor,
    editGdnAccentColor,
  } = useSetting();
  const {customBgs} = useImages();
  const isLocked = locked.some((x) => x === id);
  const layoutNumber = Number(layoutOption);
  const showButton = showContent && isButtonEnabled && GDN_BUTTON_LAYOUTS.includes(layoutNumber);
  const showTags = showContent && layoutNumber === 3;
  // Per-template custom bg wins over the shared one (only when locked).
  const customBgUrl = (isLocked ? customBgs[id] : undefined) || customBgs.main || null;

  const {
    titles,
    subtitles,
    badges,
    productNames,
    gdnTags,
    gdnTagOrder,
    addGdnTag,
    removeGdnTag,
  } = useEdits();
  const title = titles[id] || titles.main;
  const subtitle = subtitles[id] || subtitles.main;
  const buttonText = badges[id] ?? badges.main ?? "Learn more";
  const productName = resolveGdnProductName(productNames, id, product);
  const productAccentColor = resolveProductAccentColor(product.value, palettes);
  const accentColor = gdnAccentColor ?? productAccentColor;

  const [accentTextUsage, setAccentTextUsage] = useState({
    title: false,
    subtitle: false,
    productName: false,
  });
  const updateAccentTextUsage = useCallback((field, usesAccentColor) => {
    setAccentTextUsage((current) => {
      if (current[field] === usesAccentColor) return current;
      return {...current, [field]: usesAccentColor};
    });
  }, []);
  const onTitleAccentColorUsageChange = useCallback(
    (usesAccentColor) => updateAccentTextUsage("title", usesAccentColor),
    [updateAccentTextUsage]
  );
  const onSubtitleAccentColorUsageChange = useCallback(
    (usesAccentColor) => updateAccentTextUsage("subtitle", usesAccentColor),
    [updateAccentTextUsage]
  );
  const onProductNameAccentColorUsageChange = useCallback(
    (usesAccentColor) => updateAccentTextUsage("productName", usesAccentColor),
    [updateAccentTextUsage]
  );

  const getColorVariant = (key) => gdnColorVariants[getGdnColorStateKey(key)];
  const logoVariant =
    (showButton && buttonVariant) || layoutVariants?.[layoutOption] || variant;
  const logoHasEditableProductName =
    logoVariant === "horizontal" || logoVariant === "responsive-horizontal";
  const visibleBodyTexts = showTags
    ? [title, ...gdnTagOrder.map((tagId) => gdnTags[tagId])]
    : [title, subtitle];
  const hideProductName =
    logoHasEditableProductName &&
    showContent &&
    shouldHideGdnProductName(productName, product.label, visibleBodyTexts);
  const showAccentControl =
    gdnTagOrder.some((key) => getColorVariant(key) === "accent") ||
    accentTextUsage.title ||
    accentTextUsage.subtitle ||
    (logoHasEditableProductName && accentTextUsage.productName);

  const getColorCycleProps = (key) => {
    const colorVariant = getColorVariant(key);
    const controlColors = colorVariant
      ? getGdnControlColors(colorVariant, accentColor)
      : undefined;
    return {
      "data-gdn-color-key": key,
      "data-gdn-color-variant": colorVariant,
      "data-tooltip": "Option+click to recolor",
      style: controlColors
        ? {...controlColors, "--gdn-control-text-color": controlColors.color}
        : undefined,
      onClick: (event) => {
        if (!event.altKey) return;
        event.preventDefault();
        event.stopPropagation();
        cycleGdnColor(key);
      },
    };
  };

  const {url: bgUrl, markers: bgMarkers} = useBgUrl();

  const handleRemoveTag = (event, tagId) => {
    event.preventDefault();
    event.stopPropagation();
    removeGdnTag(tagId);
    clearGdnColor(tagId);
  };

  const handleAddTag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    addGdnTag("tag");
  };

  return (
    <div
      id={id}
      className={`template google-display-network tpl-card layout-${layoutOption}${showButton ? " button-enabled" : ""}`}
      style={{"--zoom-level": zoom}}
    >
      {/* Single wrapper child required by the export pipeline. */}
      <div className="tpl-card__inner">
        {customBgUrl || bgUrl ? (
          <BgTransformer
            src={customBgUrl || bgUrl}
            id={id}
            fallbackTransformId={fallbackTransformId}
            defaultTransform={defaultTransform}
            extraTools={
              showAccentControl ? (
                <GdnAccentControl
                  id={id}
                  color={accentColor}
                  hasOverride={gdnAccentColor !== null}
                  onChange={editGdnAccentColor}
                />
              ) : null
            }
          />
        ) : null}

        <GdnLogoBlock
          id={id}
          variant={logoVariant}
          productName={productName}
          hideProductName={hideProductName}
          bgMarkers={bgMarkers}
          accentColor={accentColor}
          onAccentColorUsageChange={onProductNameAccentColorUsageChange}
        />

        {showContent ? (
          <div className="tpl-content">
            <h1 className="title">
              <ContentEditable
                id={id}
                text={title}
                color={accentColor}
                highlightStyle="text-color"
                enableWhiteHighlightHotkey={false}
                onAccentColorUsageChange={onTitleAccentColorUsageChange}
              />
            </h1>
            {!showTags && <h2 className="subtitle">
              <ContentEditable
                id={id}
                text={subtitle}
                color={accentColor}
                highlightStyle="text-color"
                enableWhiteHighlightHotkey={false}
                onAccentColorUsageChange={onSubtitleAccentColorUsageChange}
              />
            </h2>}
            {showTags ? (
              <div className={`tpl-tag-line${gdnTagOrder.length === 0 ? " is-empty" : ""}`}>
                {gdnTagOrder.map((tagId) => (
                  <div
                    key={tagId}
                    {...getColorCycleProps(tagId)}
                    className={`gdn-tag tpl-tag instant-tooltip instant-tooltip--below${getColorVariant(tagId) ? " gdn-color-override" : ""}`}
                    data-gdn-tag-id={tagId}
                  >
                    <Textarea colorId={id} id={tagId} text={gdnTags[tagId]} inline />
                    <button
                      type="button"
                      className="gdn-tag-control gdn-tag-remove"
                      aria-label={`Remove ${gdnTags[tagId] || "tag"}`}
                      onClick={(event) => handleRemoveTag(event, tagId)}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="gdn-tag-control gdn-tag tpl-tag gdn-tag-add"
                  aria-label="Add tag"
                  onClick={handleAddTag}
                >
                  <span aria-hidden="true">+</span>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {showButton ? (
          <div
            {...getColorCycleProps("button")}
            className={`gdn-button tpl-cta instant-tooltip${getColorVariant("button") ? " gdn-color-override" : ""}`}
          >
            <Textarea id={id} text={buttonText} inline />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GdnAdTemplate;
